/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useMemo } from "react";

import { FullAddress } from "../../../../services/als";
import { AccountAddress } from "../../../azure-b2c/storage";

import {
  DeliveryOption,
  FreightCharge,
  FreightRange,
  SalesAgreementPromoProductSelectionVariant,
  SiteInfo,
} from "./types";
import { sortProductsSelected } from "./utils";

export interface ICampaignState {
  tabIndex: number;
  deliveryOption: DeliveryOption | undefined;
  addressSelected: AccountAddress | FullAddress | undefined;
  siteSelected: SiteInfo | undefined;
  productSelected: SalesAgreementPromoProductSelectionVariant[];
  freightRangeSelected: FreightRange | undefined;
  freightCharge: FreightCharge; // NOTE: null value for required distance select
  selectDistance: boolean;
  timer: number;
}

interface ICampaignContext {
  state: ICampaignState;
  setState: React.Dispatch<Partial<ICampaignState>>;
  reset: () => void;
}

interface CampaignContextProviderProps {
  children: React.ReactNode;
}

const CampaignContext = React.createContext<ICampaignContext | null>(null);

const initState = {
  deliveryOption: undefined,
  addressSelected: undefined,
  tabIndex: 0,
  siteSelected: undefined,
  productSelected: [],
  freightRangeSelected: undefined,
  freightCharge: undefined,
  selectDistance: false,
  timer: 0,
};

const CampaignContextProvider: React.FC<CampaignContextProviderProps> = ({ children }) => {
  const [state, setState] = React.useReducer(
    (prev: ICampaignState, next: Partial<ICampaignState>) => ({ ...prev, ...next }),
    initState
  );

  const reset = () => {
    setState(initState);
  };

  const transformedState: ICampaignState = {
    ...state,
    productSelected: sortProductsSelected(state.productSelected),
  };

  const memoizedValue = useMemo(
    () => ({ state: transformedState, setState, reset }),
    [transformedState]
  );

  return <CampaignContext.Provider value={memoizedValue}>{children}</CampaignContext.Provider>;
};

function withCampaignContextProvider<T extends object>(Component: React.ComponentType<T>) {
  return (props: T) => {
    return (
      <CampaignContextProvider>
        <Component {...props} />
      </CampaignContextProvider>
    );
  };
}

function useCampaignContext() {
  const contextValue = React.useContext(CampaignContext);
  if (contextValue === null) {
    throw Error("CampaignContext has not been Provided!");
  }
  return contextValue;
}

export { useCampaignContext, withCampaignContextProvider };
